//rfq bd.js
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Button } from "react-bootstrap";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CharacteristicsModal from '../../../Component/CharacteristicsModal ';
import { ORDER_API, MASTER_API } from "../../../apiConfig";
import { authHeaders } from "../../../utils/authHeaders";

export default function Bd({ productDetails: allProductDetails, selectedRfq, mode, canReview, onDecisionSubmitted }) {
    const [selectedIds, setSelectedIds] = useState([]);
    const [bulkDecision, setBulkDecision] = useState("");
    const [showBulkModal, setShowBulkModal] = useState(false);
    const [bulkComment, setBulkComment] = useState("");
    const [bulkReason, setBulkReason] = useState("");
    const [rejectionReasons, setRejectionReasons] = useState([]);
    const [selectedCharacteristics, setSelectedCharacteristics] = useState([]);
    const [modalVisible, setModalVisible] = useState(false);
    const [showProductModal, setShowProductModal] = useState(false);
    const [partProductDetails, setPartProductDetails] = useState("");
    const [header, setHeader] = useState("");


    // Helper to determine if we are in Customer Flow.
    // Home.js now renders three separate tabs (Review / Review for Customer /
    // Discount Review) and passes mode="internal", mode="customer", or
    // mode="discount" explicitly. mode="discount" is treated the same as
    // "internal" here (it drives the same internal_action field and the
    // same submit-decision endpoint) — Home.js is what pre-filters
    // productDetails down to just the discount-flagged lines before
    // handing them to this component. If no mode is passed (older
    // callers), fall back to the old rfq_status sniff.
    const isCustomerFlow = mode
        ? mode === 'customer'
        : selectedRfq.rfq_status.includes('Customer');

    // Discount Review now writes to its OWN column (discount_review_action)
    // via its OWN endpoint (submit-discount-decision) — it no longer drives
    // internal_action, so approving here does not auto-approve the Review
    // tab. internalDecision on the backend gates Review-tab "Approve" on a
    // discount-flagged line until discount_review_action = 'Approved'.
    const isDiscountFlow = mode === 'discount';

    // A row needs Discount Review when its Discount (%) exceeds its
    // effective Markup (%) — same comparison Home.js uses to decide
    // whether the Discount Review tab appears at all (row's own markup
    // override if it has one, else the header Markup (%)).
    const isDiscountFlagged = (p) => {
        const rowMarkup = (p.markup !== undefined && p.markup !== null && p.markup !== "")
            ? (parseFloat(p.markup) || 0)
            : (parseFloat(selectedRfq.markup || 0) || 0);
        const disc = parseFloat(p.discount || 0) || 0;
        return disc > rowMarkup;
    };

    // On the internal Review tab, a discount-flagged row is locked out of
    // Approve/Reject/Query until it has been cleared in Discount Review —
    // this mirrors the backend gate (internalDecision rejects with 401
    // otherwise) so the person sees a clear reason up front instead of a
    // raw request failure after clicking Approve.
    const isPendingDiscountReview = (p) =>
        !isCustomerFlow && !isDiscountFlow && isDiscountFlagged(p) && p.discount_review_action !== 'Approved';

    // Review for Customer only ever shows items that were Approved in the
    // internal review. Rejected/Query/Pending internal items never reach
    // the customer — they stay behind in the internal Review tab.
    const productDetails = isCustomerFlow
        ? allProductDetails.filter(p => p.internal_action === 'Approved')
        : allProductDetails;

    // Single source of truth for "what action state is this row in", per mode.
    const getCurrentAction = (p) => isCustomerFlow
        ? p.customer_approval_status
        : isDiscountFlow
            ? p.discount_review_action
            : p.internal_action;

    // Reason for Reject/Query on the customer-facing tab is picked from the
    // Customer Rejection Reason config (Product Master) rather than typed
    // freehand — fetched once when this tab is in customer flow.
    useEffect(() => {
        if (!isCustomerFlow) return;

        axiosGetRejectionReasons();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isCustomerFlow]);

    const axiosGetRejectionReasons = async () => {
        try {
            const res = await fetch(`${MASTER_API}/configuration/customer-rejection-reason`, {
                headers: { ...authHeaders() }
            });
            const data = await res.json();
            setRejectionReasons(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error("Error fetching rejection reasons:", e);
        }
    };

    const getCurrentUser = () => {
        // Try full user object first (your login stores: { email, role, token, ... })
        const userObj = localStorage.getItem("user");
        if (userObj) {
            try {
                const parsedUser = JSON.parse(userObj);
                // Prefer the actual user id (e.g. "maithili") so Approved By / review
                // stamps store the user id, not the name or email.
                if (parsedUser.userId) return parsedUser.userId;
                // Then name fields, fallback to email
                const fullName = `${parsedUser.firstName || ''} ${parsedUser.lastName || ''}`.trim();
                if (fullName) return fullName;
                if (parsedUser.email) return parsedUser.email;
            } catch (e) { console.error("Error parsing user data:", e); }
        }
        // Also check if token is stored separately and email is stored flat
        const storedUserId = localStorage.getItem("userId");
        if (storedUserId && storedUserId !== "undefined" && storedUserId !== "null") return storedUserId;
        const email = localStorage.getItem("email");
        if (email && email !== "undefined" && email !== "null") return email;
        const storedName = localStorage.getItem("userName");
        if (storedName && storedName !== "undefined" && storedName !== "null") return storedName;
        return "User";
    };
    const handleSelectProduct = (e, id) => {
        if (e.target.checked) {
            setSelectedIds(prev => [...prev, id]);
        } else {
            setSelectedIds(prev => prev.filter(item => item !== id));
        }
    };

    const handleSelectAll = (checked) => {
        if (checked) {
            // Filter logic: Select everything that is NOT disabled
            const idsToSelect = productDetails
                .filter(p => {
                    const currentAction = getCurrentAction(p);
                    // Internal/Discount Flow: Select Pending / Query only. Approved and
                    // Rejected are FINAL — locking both prevents a later "Select All +
                    // Approve" from silently flipping an already-Rejected row back to
                    // Approved. Customer Flow: Select Pending only. Rows still waiting
                    // on Discount Review are also excluded on the internal tab.
                    if (isPendingDiscountReview(p)) return false;
                    return isCustomerFlow ? !currentAction : (currentAction !== 'Approved' && currentAction !== 'Rejected');
                })
                .map(p => p.id);
            setSelectedIds(idsToSelect);
        } else {
            setSelectedIds([]);
        }
    };

    const submitDecision = async () => {
        if (selectedIds.length === 0) return;

        if (isDiscountFlow) {
    const status = selectedRfq?.rfq_status || "";

    const isSubmitted =
        status !== "Draft" &&
        status !== "Created" &&
        status !== "";

    if (!isSubmitted) {
        toast.error("Please submit the RFQ first.", {
            autoClose: 3000
        });
        return;
    }
}

        // Defensive guard — selectedIds should never contain a row still
        // pending Discount Review (checkboxes are disabled for those), but
        // block it here too rather than letting the backend's 401 surface
        // as a raw "Failed to submit decision" toast.
        if (!isCustomerFlow && !isDiscountFlow) {
            const blocked = productDetails.filter(p => selectedIds.includes(p.id) && isPendingDiscountReview(p));
            if (blocked.length > 0) {
                toast.error("Some selected items are pending Discount Review. Please clear them in the Discount Review tab first.");
                return;
            }
        }

        const url = isCustomerFlow
            ? `${ORDER_API}/quotation/${selectedRfq.rfq_id}/submit-customer-decision`
            : isDiscountFlow
                ? `${ORDER_API}/quotation/${selectedRfq.rfq_id}/submit-discount-decision`
                : `${ORDER_API}/quotation/${selectedRfq.rfq_id}/submit-decision`;

        try {
            // ================= LOOP ALL SELECTED IDs =================
            for (const lineItemId of selectedIds) {
                const response = await fetch(url, {
                    method: "POST",
                    headers: { "Content-Type": "application/json", ...authHeaders() },
                    body: JSON.stringify({
                        lineItemId: lineItemId,   // primary id of rfq row
                        action: bulkDecision,
                        comment: bulkComment,
                        reason: bulkReason,       // Customer Rejection Reason config pick — saved separately
                        user: getCurrentUser()
                    }),
                });

                if (!response.ok) {
                    toast.error(`Failed to submit decision for item ${lineItemId}`);
                    return;
                }
            }

            toast.success("Decision submitted successfully.");

            // Close the modal and reset its inputs so the table underneath
            // is what's visible — that table's "Reason"/"Status" columns
            // already render internal_action_comment / customer_approval_comment
            // / discount_review_comment per row, so the comment just entered
            // shows up right there once the data is refreshed below.
            setShowBulkModal(false);
            setSelectedIds([]);
            setBulkComment("");
            setBulkReason("");

            // Refresh in place instead of a full page reload — a hard
            // reload was dumping the user back out to the RFQ list, so the
            // comment they just entered was never visible without manually
            // reopening the same RFQ. If no refresh callback was passed in,
            // fall back to the old reload behavior.
            if (onDecisionSubmitted) {
                await onDecisionSubmitted();
            } else {
                setTimeout(() => window.location.reload(), 1500);
            }

        } catch (error) {
            toast.error("Error submitting decision");
        }
    };

    const toggleRowExpansion = (part) => {
        if (part.characteristics) {
            let chars = part.characteristics;
            if (typeof chars === 'string') {
                try { chars = JSON.parse(chars); } catch (e) { chars = []; }
            }
            setSelectedCharacteristics(chars);
            setModalVisible(true);
        }
    };

    const handleViewProductDetails = async (id) => {
        try {
            const res = await fetch(`${ORDER_API}/quotation/line-item/${id}`, {
                headers: { ...authHeaders() }
            });

            if (res.ok) {
                const data = await res.json();
                const cgstAmount = Number(data.cgst_amount || 0);
                const sgstAmount = Number(data.sgst_amount || 0);
                const igstAmount = Number(data.igst_amount || 0); // ✅ Add IGST extraction
                const netPrice = Number(data.net_price_after_discount || 0);
                const unitPrice = Number(data.unit_price || 0);
                const quantity = Number(data.quantity || 0);

                setPartProductDetails({
                    ...data,
                    hsnCode: data.hsn_code,
                    netPrice: netPrice,
                    cgstAmount: cgstAmount,
                    sgstAmount: sgstAmount,
                    igstAmount: igstAmount, // ✅ Store IGST amount
                    totalGST: cgstAmount + sgstAmount + igstAmount, // ✅ Include IGST in total
                    finalPriceAfterGST: Number(data.final_price_after_gst || 0),
                    totalPrice: unitPrice * quantity
                });

                setShowProductModal(true);
            } else {
                toast.error("Product details not found");
            }
        } catch (e) {
            console.error("Fetch Error:", e);
            toast.error("Error fetching details");
        }
    };

    const handleChange = () => {
        toast.info("You can change it here, in review you can't change");
    };

    const formatCurrency = (value) => {
        const val = Number(value);
        if (isNaN(val)) return "0";
        return val.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    // Calculate selectable items count for Header Checkbox logic
    const selectableItemsCount = productDetails.filter(p => {
        const currentAction = getCurrentAction(p);
        if (isPendingDiscountReview(p)) return false;
        return isCustomerFlow ? !currentAction : (currentAction !== 'Approved' && currentAction !== 'Rejected');
    }).length;

    // Count of rows on the internal Review tab still waiting on Discount
    // Review — drives the banner below the action buttons.
    const pendingDiscountReviewCount = !isCustomerFlow && !isDiscountFlow
        ? productDetails.filter(isPendingDiscountReview).length
        : 0;

    return (
        <div>
            <hr />
            {/* Header-level Submission Comment — typed once on the original
                Submit-for-Approval dialog and saved against every line item
                (submission_comment column), but shown here ONCE as a banner
                rather than repeated in every row's Reason cell, since it's
                one comment for the whole RFQ, not a per-line decision. */}
            {selectedRfq?.submission_comment && (
                <div style={{
                    background: '#f0f9ff', border: '1px solid #bae6fd', color: '#0c4a6e',
                    borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 13
                }}>
                    <b>Submission Comment:</b> {selectedRfq.submission_comment}
                </div>
            )}
            {pendingDiscountReviewCount > 0 && (
                <div style={{
                    background: '#fff7ed', border: '1px solid #fed7aa', color: '#9a3412',
                    borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 13
                }}>
                    ⚠ {pendingDiscountReviewCount} item{pendingDiscountReviewCount > 1 ? 's' : ''} exceed{pendingDiscountReviewCount > 1 ? '' : 's'} their Markup (%) with a higher Discount (%) and need to be cleared in the <b>Discount Review</b> tab before they can be approved here.
                </div>
            )}
            {/* CHANGE: Agar Customer Flow hai toh buttons Left (flex-start), nahi toh Right (flex-end) */}
            <div className="d-flex gap-2 mb-3" style={{ justifyContent: isCustomerFlow ? 'flex-start' : 'flex-end' }}>
              <Button
    style={styles.primaryBtn}
    variant="dark"
    disabled={selectedIds.length === 0 || !canReview}
    title={!canReview ? "Your role does not have Review access" : undefined}
    onClick={() => {

        // Discount Approval only after RFQ is submitted
        if (isDiscountFlow) {
            const status = selectedRfq?.rfq_status || "";

            const isSubmitted =
                status !== "Draft" &&
                status !== "Created" &&
                status !== "";

            if (!isSubmitted) {
                toast.error("Please submit the RFQ first.", {
                    autoClose: 3000
                });
                return;
            }
        }

        setBulkDecision("Approved");
        setHeader("Approve");
        setBulkComment("");
        setBulkReason("");
        setShowBulkModal(true);
    }}
>
    Approve
</Button>
                <Button
                    style={styles.primaryBtn}
                    variant="dark"
                    disabled={selectedIds.length === 0 || !canReview}
                    title={!canReview ? "Your role does not have Review access" : undefined}
                    onClick={() => {
                        setBulkDecision("Rejected");
                        setHeader("Reject");
                        setBulkComment("");
                        setBulkReason("");
                        setShowBulkModal(true);
                    }}
                >
                    Reject
                </Button>
                <Button
                    style={styles.primaryBtn}
                    variant="dark"
                    disabled={selectedIds.length === 0 || !canReview}
                    title={!canReview ? "Your role does not have Review access" : undefined}
                    onClick={() => {
                        setBulkDecision("Query");
                        setHeader("Raise Query");
                        setBulkComment("");
                        setBulkReason("");
                        setShowBulkModal(true);
                    }}
                >
                    Query
                </Button>
            </div>

            <div style={styles.tableContainer}>
                <table className="table mb-0" style={styles.erpTable}>
                    <thead>
                        <tr>
                            <th style={styles.erpTh}>
                                <input
                                    type="checkbox"
                                    onChange={(e) => handleSelectAll(e.target.checked)}
                                    checked={
                                        selectedIds.length > 0 &&
                                        selectedIds.length === selectableItemsCount
                                    }
                                />
                            </th>
                            <th style={styles.erpTh}>Item</th>
                            <th style={styles.erpTh}>Product Code</th>
                            <th style={styles.erpTh}>Description</th>
                            <th style={styles.erpTh}>Fast Entry</th>
                            <th style={styles.erpTh}>UOM</th>
                            <th style={styles.erpTh}>Unit Price</th>
                            <th style={styles.erpTh}>Qty</th>
                            <th style={styles.erpTh}>Total Price</th>
                            <th style={styles.erpTh}>Req. Date</th>
                            <th style={styles.erpTh}>Disc (%)</th>
                            <th style={styles.erpTh}>Status</th>
                            <th style={styles.erpTh}>Reason</th>
                            <th style={styles.erpTh}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {productDetails.length === 0 && (
                            <tr>
                                <td colSpan={14} style={{ ...styles.erpTd, textAlign: "center", color: "#94a3b8" }}>
                                    {isCustomerFlow ? "No approved items to review yet." : "No items to review."}
                                </td>
                            </tr>
                        )}
                        {productDetails.map((part, idx) => {
                            const currentAction = getCurrentAction(part);
                            const pendingDiscount = isPendingDiscountReview(part);

                            // ✅ FIXED LOGIC:
                            // Internal/Discount Flow: Approved AND Rejected are FINAL — disable
                            // both. Only Pending/Query rows stay editable. Rows still awaiting
                            // Discount Review are also locked here (see isPendingDiscountReview).
                            // Customer Flow: Disable if any decision exists.
                            const isCheckboxDisabled = isCustomerFlow
                                ? !!currentAction
                                : (currentAction === 'Approved' || currentAction === 'Rejected' || pendingDiscount);

                            return (
                                <tr key={part.id} style={{ opacity: isCheckboxDisabled ? 0.6 : 1 }}>
                                    <td style={styles.erpTd}>
                                        <input
                                            type="checkbox"
                                            disabled={isCheckboxDisabled}
                                            checked={selectedIds.includes(part.id)}
                                            onChange={(e) => handleSelectProduct(e, part.id)}
                                            title={pendingDiscount ? "Pending Discount Review — clear it in the Discount Review tab first" : undefined}
                                        />
                                    </td>
                                    <td style={styles.erpTd}>{idx + 1}</td>
                                    <td style={styles.erpTd}>{part.product_code}</td>
                                    <td style={styles.erpTd}>{part.description}</td>
                                    <td style={styles.erpTd}>
                                        {part.product_class}
                                        <span
                                            style={{ cursor: 'pointer', fontSize: '14px', marginLeft: '6px', color: '#0d0d0e' }}
                                            onClick={() => toggleRowExpansion(part)}
                                        >
                                            ▶
                                        </span>
                                    </td>
                                    <td style={styles.erpTd}>{part.uom}</td>
                                    <td style={styles.erpTd}>{Number(part.unit_price).toFixed(2)}</td>
                                    <td style={styles.erpTd}>{part.quantity}</td>
                                    <td style={styles.erpTd}>{formatCurrency(Number(part.total_price).toFixed(2))}</td>
                                    {/* <td style={styles.erpTd}>
                                        {part.requirement_date ? new Date(part.requirement_date).toLocaleDateString("en-GB") : ""}
                                    </td> */}
                                    <td style={styles.erpTd}>
                                        {part.requirement_date ? (() => {
                                            const d = new Date(part.requirement_date);
                                            const day = String(d.getDate()).padStart(2, '0');
                                            const month = String(d.getMonth() + 1).padStart(2, '0');
                                            const year = d.getFullYear();
                                            return `${day}/${month}/${year}`;
                                        })() : ""}
                                    </td>
                                    <td style={styles.erpTd}>{part.discount}</td>
                                    <td style={{ ...styles.erpTd, color: pendingDiscount ? '#c2410c' : undefined, fontWeight: pendingDiscount ? 600 : undefined }}>
                                        {pendingDiscount ? "Pending Discount Review" : (currentAction || "Pending")}
                                    </td>

                                    <td
                                        style={{
                                            ...styles.erpTd,
                                            color:
                                                (currentAction || "").toLowerCase().includes("approve")
                                                    ? "green"
                                                    : (currentAction || "").toLowerCase().includes("reject")
                                                        ? "red"
                                                        : "#334155"
                                        }}
                                    >
                                        {isCustomerFlow
                                            ? (part.customer_rejection_reason || part.customer_approval_comment || "-")
                                            : isDiscountFlow
                                                ? (part.discount_review_comment || "-")
                                                : (part.internal_action_comment || "-")}
                                    </td>
                                    <td style={styles.erpTd}>
                                        <button className="btn btn-sm btn-outline-dark" onClick={() => handleViewProductDetails(part.id)}>
                                            View
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <CharacteristicsModal
                modalVisible={modalVisible}
                selectedCharacteristics={selectedCharacteristics}
                handleClose={() => setModalVisible(false)}
                handleChange={handleChange}
                isEditing={false}
            />

            {showProductModal && partProductDetails && (
                <div style={styles.modalBackdrop}>
                    <div style={{ ...styles.modal, maxWidth: "950px", width: "95%", maxHeight: "85vh", padding: "14px", fontSize: "13px" }}>
                        <div style={{ padding: "12px" }}>
                            <table className="table table-bordered table-sm mb-3">
                                <thead style={{ background: "#f8fafc", fontWeight: 600 }}>
                                    <tr>
                                        <th>Product Code</th>
                                        <th>HSN Code</th>
                                        <th>Quantity</th>
                                        <th>Unit Price</th>
                                        <th>Discount (%)</th>
                                        <th>Total Price</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>{partProductDetails.product_code}</td>
                                        <td>{partProductDetails.hsnCode}</td>
                                        <td>{partProductDetails.quantity}</td>
                                        <td>₹ {partProductDetails.unit_price}</td>
                                        <td>{partProductDetails.discount}</td>
                                        <td style={{ fontWeight: 600 }}>₹ {formatCurrency(partProductDetails.totalPrice)}</td>
                                    </tr>
                                </tbody>
                            </table>
                            <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 10px", borderTop: "1px solid #999", borderBottom: "1px solid #999", marginBottom: "10px", fontWeight: 600 }}>
                                <span>Net Price :</span>
                                <span>₹ {formatCurrency(partProductDetails.netPrice)}</span>
                            </div>
                            <table className="table table-sm" style={{ width: "50%", marginLeft: "auto" }}>
                                <table className="table table-sm" style={{ width: "50%", marginLeft: "auto" }}>
                                    <tbody>
                                        <tr>
                                            <td>CGST @ {partProductDetails.cgst}%</td>
                                            <td style={{ textAlign: "right" }}>₹ {formatCurrency(partProductDetails.cgstAmount)}</td>
                                        </tr>
                                        <tr>
                                            <td>SGST @ {partProductDetails.sgst}%</td>
                                            <td style={{ textAlign: "right" }}>₹ {formatCurrency(partProductDetails.sgstAmount)}</td>
                                        </tr>
                                        {/* ✅ Add IGST Row Here */}
                                        <tr>
                                            <td>IGST @ {partProductDetails.igst}%</td>
                                            <td style={{ textAlign: "right" }}>₹ {formatCurrency(partProductDetails.igstAmount)}</td>
                                        </tr>
                                        <tr style={{ borderTop: "1px solid #000", fontWeight: 600 }}>
                                            <td>Total GST</td>
                                            <td style={{ textAlign: "right" }}>₹ {formatCurrency(partProductDetails.totalGST)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </table>
                            <div style={{ display: "flex", justifyContent: "space-between", padding: "12px", marginTop: "8px", background: "#f1f5f9", fontWeight: 700, fontSize: "15px", borderRadius: "4px" }}>
                                <span>Grand Total :</span>
                                <span>₹ {formatCurrency(partProductDetails.finalPriceAfterGST)}</span>
                            </div>
                        </div>
                        <div style={{ ...styles.modalFooter, padding: "10px 14px" }}>
                            <button className="btn btn-outline-secondary btn-sm" onClick={() => setShowProductModal(false)}>Close</button>
                        </div>
                    </div>
                </div>
            )}

            {showBulkModal && (
                <div style={styles.modalBackdrop}>
                    <div style={{ ...styles.modal, maxWidth: "600px", width: "100%" }}>
                        <div style={styles.modalHeader}>
                            <div style={{ fontWeight: 600 }}>{`${header} RFQ `}</div>
                            <button style={styles.iconBtn} onClick={() => setShowBulkModal(false)}>✕</button>
                        </div>
                        <div style={styles.modalBody}>
                            {isCustomerFlow && bulkDecision === "Rejected" ? (
                                <>
                                    <label style={{ display: "block", fontWeight: 600, marginBottom: "6px" }}>
                                        Customer Rejection Reason
                                    </label>
                                    <select
                                        value={bulkReason}
                                        onChange={(e) => {
                                            const selectedReason = e.target.value;
                                            setBulkReason(selectedReason);
                                            // The dropdown pick becomes the comment itself, so the
                                            // person doesn't have to retype the same reason in the
                                            // textarea below. They can still edit/append afterwards.
                                            setBulkComment(selectedReason);
                                        }}
                                        style={{ width: "100%", padding: "10px", borderRadius: "5px", border: "1px solid #ccc" }}
                                    >
                                        <option value="">Select Customer Rejection Reason</option>
                                        {rejectionReasons.map((r) => (
                                            <option key={r.id} value={r.reason}>{r.reason}</option>
                                        ))}
                                    </select>
                                    <textarea
                                        value={bulkComment}
                                        onChange={(e) => setBulkComment(e.target.value)}
                                        placeholder="Enter your comment here"
                                        rows="4"
                                        style={{ width: "100%", padding: "10px", borderRadius: "5px", border: "1px solid #ccc", resize: "none", marginTop: "12px" }}
                                    />
                                </>
                            ) : (
                                <textarea
                                    value={bulkComment}
                                    onChange={(e) => setBulkComment(e.target.value)}
                                    placeholder="Enter your comment here"
                                    rows="4"
                                    style={{ width: "100%", padding: "10px", borderRadius: "5px", border: "1px solid #ccc", resize: "none" }}
                                />
                            )}
                        </div>
                        <div style={styles.modalFooter}>
                            <button className="btn btn-outline-secondary" onClick={() => setShowBulkModal(false)}>Cancel</button>
                            <button
                                className="btn btn-dark"
                                disabled={isCustomerFlow && bulkDecision === "Rejected" && !bulkReason}
                                onClick={() => submitDecision(bulkDecision)}
                            >
                                Submit
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

Bd.propTypes = {
    productDetails: PropTypes.array.isRequired,
    selectedRfq: PropTypes.shape({
        rfq_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        rfq_status: PropTypes.string.isRequired,
    }).isRequired,
    mode: PropTypes.oneOf(['internal', 'customer', 'discount']),
    canReview: PropTypes.bool,
    onDecisionSubmitted: PropTypes.func,
};

const styles = {
    primaryBtn: { paddingLeft: 14, paddingRight: 14 },
    tableContainer: { background: "#ffffff", borderRadius: 8, padding: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.08)" },
    erpTable: { width: "100%", tableLayout: "auto", borderCollapse: "collapse" },
    erpTh: { background: "#f8fafc", fontSize: 13, fontWeight: 600, padding: "10px 12px", borderBottom: "1px solid #e5e7eb", color: "#1e293b", whiteSpace: "nowrap" },
    erpTd: { fontSize: 13, padding: "10px 12px", borderBottom: "1px solid #f1f5f9", color: "#334155", verticalAlign: "middle" },
    modalBackdrop: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050, padding: '20px' },
    modal: { background: '#fff', borderRadius: '8px', width: '90%', maxWidth: '1200px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
    modalHeader: { padding: '15px 20px', borderBottom: '1px solid #ddd', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    iconBtn: { background: 'transparent', border: 'none', fontSize: '18px', cursor: 'pointer' },
    modalBody: { padding: '20px', overflowY: 'auto', flex: 1 },
    modalFooter: { padding: '15px 20px', borderTop: '1px solid #ddd', display: 'flex', justifyContent: 'flex-end', gap: '10px' },
};